import AuthApi from "../../data/api/authApi";
import * as React from "react";
import { connect } from "react-redux";

// import FacebookLogin from 'react-facebook-login';
import { useLocation, useNavigate } from "react-router";
import { Button, Card, Stack, Text, TextInput } from "@mantine/core";
import { getSelf } from "../../data/actions/personActions";
import { resetPasswordTokenType } from "../../data/actions/tokenActions";
import TokenApi from "../../data/api/tokenApi";
import { IPersonLite } from "../../types/persons/person";
import { IToken } from "../../types/token/Token";
import { CenteredLoader } from "../common/loader";
import {
  PasswordValidator,
  validateAllPasswordConstraints,
} from "./PasswordValidator";

const classes: { [key: string]: React.CSSProperties } = {
  background: {
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
  },
  paper: {
    padding: "30px",
    marginTop: "30px",
    minWidth: "300px",
    maxWidth: "500px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  fullWidth: { width: "100%" },
  center: {
    marginLeft: "auto",
    marginRight: "auto`",
  },
  facebookButton: {
    backgroundColor: "#4c69ba",
    borderColor: "#4c69ba",
    width: "100%",
    borderRadius: "5px",
    textTransform: "uppercase",
    display: "inlineBlock",
    fontWeight: "700",
    WebkitFontSmoothing: "antialiased",
    color: "#fff",
    padding: "10px",
  },
};

interface IResetPasswordProps {
  // facebook thing
  // code?: string
  classes: { background: any; paper: any; fullWidth: any; center: any };
  actions: {
    getSelf: () => Promise<any>;
  };
}

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword: React.FunctionComponent<IResetPasswordProps> = (props) => {
  const [password, setPassword] = React.useState<string>("");
  const [confirmation, setConfirmation] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [person, setPerson] = React.useState<IPersonLite | null>();
  const [error, setError] = React.useState<string | null>();
  const [token, setToken] = React.useState<IToken | null>();

  const navigate = useNavigate();
  const tokenId = useQuery().get("token");
  const title = <h2 style={classes.center}>Reset Password</h2>;

  // SECURITY: Validate token format
  const isValidTokenFormat = (token: string | null): boolean => {
    if (!token || typeof token !== 'string') return false;
    // Basic validation - should be hexadecimal and between 24 and 100 characters
    return /^[a-fA-F0-9]{24,100}$/.test(token);
  };

  React.useEffect(() => {
    if (!tokenId) return;
    
    // SECURITY: Validate token format before making API call
    if (!isValidTokenFormat(tokenId)) {
      setError('Invalid token format');
      return;
    }
    
    setIsLoading(true);
    TokenApi.getToken(tokenId, resetPasswordTokenType)
      .then((resp) => {
        setToken(resp.token);
        setPerson(resp.person);
        setIsLoading(false);
      })
      .catch((er) => {
        setIsLoading(false);
        // SECURITY: Sanitize error message to prevent XSS
        const sanitizedError = (er.message || 'An error occurred').replace(/[<>&"']/g, '').slice(0, 200);
        setError(sanitizedError);
      });
  }, [tokenId]);

  if (error || !tokenId || !isValidTokenFormat(tokenId)) {
    return (
      <div style={classes.background}>
        <Card style={classes.paper}>
          {title}
          <p className="text-danger">
            {/* SECURITY: Sanitize error message display */}
            {(error || "An invalid password token was set").replace(/[<>&"']/g, '')}
          </p>
        </Card>
      </div>
    );
  }

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    
    // SECURITY: Validate inputs before submission
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (password !== confirmation) {
      setError('Password confirmation does not match');
      return;
    }
    
    // SECURITY: Additional token validation at submission
    if (!tokenId || !isValidTokenFormat(tokenId)) {
      setError('Invalid or missing token');
      return;
    }
    
    try {
      const result = await AuthApi.updatePasswordWithToken(tokenId, password, confirmation);
      
      if (result.success) {
        // SECURITY: Navigate to safe location
        navigate("/");
      } else {
        // SECURITY: Sanitize error message
        const sanitizedError = (result.error || result.message || 'Password update failed')
          .replace(/[<>&"']/g, '').slice(0, 200);
        setError(sanitizedError);
      }
    } catch (error) {
      // SECURITY: Sanitize error message
      const sanitizedError = (error instanceof Error ? error.message : 'Network error occurred')
        .replace(/[<>&"']/g, '').slice(0, 200);
      setError(sanitizedError);
    }
  };

  if (isLoading) return <CenteredLoader />;

  return (
    <Card style={classes.paper}>
      {title}
      <PasswordValidator password={password} confirmation={confirmation} />
      <form onSubmit={submitPasswordChange}>
        {/* <input style={{display: "hidden"}} name="username" */}

        <Stack gap="sm">
          <TextInput
            label="New password"
            id="password"
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Stack>
        <Stack gap="sm">
          <TextInput
            label="Confirm new password"
            id="confirmation"
            placeholder="Confirm Password"
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
          />
        </Stack>
        <p className="text-danger">
          {/* SECURITY: Ensure error message is sanitized */}
          {error ? error.replace(/[<>&"']/g, '') : ''}
        </p>
        <br />
        <Button
          style={classes.fullWidth}
          type="submit"
          disabled={!validateAllPasswordConstraints(password, confirmation)}
        >
          Reset password
        </Button>
      </form>
    </Card>
  );
};

/*
<div id="fb-root"></div>
	<script async defer crossOrigin="anonymous" src="https://connect.facebook.net/en_GB/sdk.js#xfbml=1&version=v3.3&appId=1036820663174621&autoLogAppEvents=1"></script>
	{title}
	<div style={classes.fullWidth}>
		<PersonAvatar style={classes.center} image={person && person.image} size={ImageSize.small} />
		<p style={classes.center} style={{textAlign: 'center'}}>{person && person.fullName}</p>
	</div>

	<p style={{color: "gray", textAlign: "center"}}>By logging in with a social platform, there's no passwords to remember.</p>
	<div style={{margin: "auto"}}>
	<FacebookLogin
		appId="1036820663174621"
		// autoLoad={true}
		fields="name,email,picture"
		onClick={this.facebookSocialAuthClicked}
		callback={this.facebookSocialAuthCallback}
		textButton="Continue with Facebook"
		icon="fa-facebook"
		responseType="token"
		cssClass={classes.facebookButton}
		state={this.props.tokenId}
		redirectUri="https://stadiumsupervisor.com.au/resetpassword"
	/>
	</div>

	<TextSeparator>or</TextSeparator>

	facebookSocialAuthClicked(e) {
		console.log(e)
	}

	facebookSocialAuthCallback(e) {
		this.connectFacebookToken(e.accessToken)
	}

	connectFacebookToken(facebookToken) {
		return AuthApi.connectFacebookAccountWithResetToken(this.props.tokenId, facebookToken)
			.then(success => {
				const message = "Your facebook account has been successfully connected, redirecting in 2 seconds"
				this.setState(state => ({ ...state, successMessage: message }))
				this.props.actions.getSelf()
					.then(() => this.props.history.push("/"))

			}).catch(er => this.setState(state => ({ ...state, error: er.message })))
	}

	*/

function mapStateToProps(state, props) {
  return {};
}

function mapDispatchToProps(dispatch): Partial<IResetPasswordProps> {
  return {
    actions: {
      getSelf: () => dispatch(getSelf()),
    },
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
